'use client'

import { Activity, Bell, ChevronsUpDown, Copy, LockKeyholeOpen, LogOut, User } from 'lucide-react'

import LogoutModal from '@/components/modal/logout-modal'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import type { Role } from '@/enum/global.enum'
import useSidebar from '@/hooks/use-sidebar'
import { isOrgRole } from '@/utils/role-check.utils'
import { useAuth } from '@clerk/clerk-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { useMe } from '@/hooks/use-me'

export function NavUser({
  name,
  email,
  imageUrl,
  role,
}: {
  name?: string
  email: string
  imageUrl: string
  role: Role
}) {
  const { me } = useMe()
  const { getToken } = useAuth()
  const { isMobile, setOpenMobile } = useSidebar()
  const [isLogout, setIsLogout] = useState(false)

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false)
  }

  const handleGetToken = async () => {
    const token = await getToken({ template: 'api-testing' })
    if (token) {
      // copy to clipboard
      navigator.clipboard.writeText(token)
      toast.success('Token copied to clipboard!')
    } else {
      toast.error('Failed to retrieve token')
    }
  }

  return (
    <div>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={imageUrl} alt={name ?? 'pp'} />
                  <AvatarFallback className='rounded-lg uppercase'>
                    {name?.length && name?.length > 1 ? (
                      <>
                        {name?.charAt(0)}
                        {name?.charAt(1)}
                      </>
                    ) : (
                      <>CY</>
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  {name && <span className='truncate font-medium'>{name}</span>}
                  <span className='truncate text-xs'>{email}</span>
                </div>
                <ChevronsUpDown id='nav-user-trigger' className='ml-auto size-4' />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
              side={isMobile ? 'bottom' : 'right'}
              align='end'
              sideOffset={4}
            >
              <DropdownMenuLabel className='p-0 font-normal'>
                <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                  <Avatar className='h-8 w-8 rounded-lg'>
                    <AvatarImage src={imageUrl} alt={name} />
                    <AvatarFallback className='rounded-lg uppercase'>
                      {name?.length && name?.length > 1 ? (
                        <>
                          {name?.charAt(0)}
                          {name?.charAt(1)}
                        </>
                      ) : (
                        <>CY</>
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className='grid flex-1 text-left text-sm leading-tight'>
                    {name && <span className='truncate font-medium'>{name}</span>}
                    <span className='truncate text-xs'>{email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {import.meta.env.VITE_ENVIRONMENT === 'DEVELOPMENT' && (
                  <DropdownMenuItem onClick={handleGetToken} className='cursor-pointer'>
                    <Copy />
                    Get Token
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={closeMobileSidebar}>
                  <Link to='/profile' className='flex w-full items-center gap-2'>
                    <User />
                    Profile
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {me?.isSSOEnable ? null : (
                  <DropdownMenuItem onClick={closeMobileSidebar}>
                    <Link to='/update-password' className='flex w-full items-center gap-2'>
                      <LockKeyholeOpen className='mb-1' />
                      Update Password
                    </Link>
                  </DropdownMenuItem>
                )}
                {isOrgRole(role) ? (
                  <DropdownMenuItem onClick={closeMobileSidebar}>
                    <Link to='/notifications' className='flex w-full items-center gap-2'>
                      <Bell />
                      Notifications
                    </Link>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={closeMobileSidebar}>
                  <Link to='/activity-logs' className='flex w-full items-center gap-2'>
                    <Activity className='mb-1' />
                    My Activity Logs
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className='cursor-pointer w-full'
                onClick={() => {
                  setIsLogout(true)
                }}
              >
                <LogOut />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <LogoutModal open={isLogout} setOpen={setIsLogout} />
    </div>
  )
}
